export type AuthSessionEvent = { type: 'token'; accessToken: string } | { type: 'logout' }

const CHANNEL_NAME = 'pelgo-auth-session'
const LOGOUT_SIGNAL_KEY = 'pelgo:auth:logout'

let accessToken: string | null = null
const listeners = new Set<(event: AuthSessionEvent) => void>()
const channel =
  typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined'
    ? new window.BroadcastChannel(CHANNEL_NAME)
    : null

function notify(event: AuthSessionEvent): void {
  for (const listener of listeners) listener(event)
}

channel?.addEventListener('message', (message: MessageEvent<AuthSessionEvent>) => {
  if (message.data?.type === 'token' || message.data?.type === 'logout') notify(message.data)
})

if (typeof window !== 'undefined') {
  // Remove tokens persisted by older frontend releases.
  try {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    localStorage.removeItem('auth-storage')
  } catch {
    // Web storage may be disabled; tokens still remain memory-only.
  }

  window.addEventListener('storage', (event) => {
    if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) notify({ type: 'logout' })
  })
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string, broadcast = true): void {
  accessToken = token
  if (broadcast)
    channel?.postMessage({ type: 'token', accessToken: token } satisfies AuthSessionEvent)
}

export function clearAccessToken(broadcast = true): void {
  accessToken = null
  if (!broadcast) return

  channel?.postMessage({ type: 'logout' } satisfies AuthSessionEvent)
  if (typeof window !== 'undefined') {
    // The transient, non-secret storage event covers browsers without BroadcastChannel.
    try {
      localStorage.setItem(LOGOUT_SIGNAL_KEY, crypto.randomUUID())
      localStorage.removeItem(LOGOUT_SIGNAL_KEY)
    } catch {
      // BroadcastChannel already covers modern browsers when storage is disabled.
    }
  }
}

export function subscribeToAuthSession(listener: (event: AuthSessionEvent) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
